import { useState } from 'react';
import { styled } from 'styled-components';

import { useWindowSize } from '@uidotdev/usehooks';

import { getSocket } from '@src/App';
import { _useDispatch, _useSelector } from '@src/state/hooks';
import { setStage } from '@src/state/slices/slice_app';
import { Stage } from '@src/state/slices/types';

import SmallButton from '@src/components/styled/SmallButton';
import EditProfilePanel from './EditProfilePanel';
import pencilEditImg from '@src/asset/img/pencil_edit.png';
import sankogoldImg from '@src/asset/img/sankogold.png';
import profileUpdate from '@src/io/events_out/_profileUpdate';
import useDMTBalance from '@src/hooks/useDMTBalance';
import { useAccount } from 'wagmi';

const Container = styled.div`
  // height: 100pt;

  margin-top: 10pt;
  margin-left: 10pt;
  margin-right: 10pt;
  padding: 10pt;
  border: 2pt solid gray;

  display: flex;

  align-items: center;
  justify-content: space-between;
  flex-direction: row;

  -khtml-user-select: none;
  -o-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  user-select: none;

  // color hyperlink blue
  color: #0000ff;

  background-color: #c0c0c0;

  @media only screen and (max-width: 720px) {
    flex-wrap: wrap;
    align-items: center;
    // justify-content: center;
    gap: 10pt;

    font-size: 3rem;
  }
`;

const SmallText = styled.div`
  font-size: 1.75rem;
  color: #008800;
  // max-width: 20rem;
  text-align: center;

  @media only screen and (max-width: 720px) {
    font-size: 1.25rem;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  gap: 5pt;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  gap: 10pt;

  @media only screen and (max-width: 720px) {
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 10pt;
    flex-direction: column;

    button {
      font-size: 1.25rem;
    }
  }
`;

const Avatar = styled.img`
  max-height: 3rem;
  width:auto;
  aspect-ratio: 1/1;
  margin-right: 5pt;
  border: none;
  object-fit: cover;

  border-right: 2pt solid black;

  -khtml-user-select: none;
  -o-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;

  @media only screen and (max-width: 720px) {
    max-height: 2.5rem;
  }
`;

const PlayerName = styled.div`
  color: black;
  font-size: 1.25rem;
  padding: 5pt;
  overflow: hidden;

  min-width: 0;

  @media only screen and (max-width: 720px) {
    font-size: 1.25rem;
    max-width: 3.5rem;
  }
`;

const PlayerMetadata = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  border: 2pt solid black;

  -khtml-user-select: none;
  -o-user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;

  min-width: 98%;

  @media only screen and (max-width: 720px) {
    min-width: 7.8rem;
    max-width: 7.8rem;
    justify-content: space-between;
  }
`;

const PlayerInfoLeft = styled.div`
  display: flex;
  align-items: center;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2pt;
  display: flex;
  align-items: center;
  justify-content: center;

  margin-right: 5pt;

  &:hover {
    opacity: 0.7;
  }
`;

const EditIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const BalanceDisplay = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5pt;
  padding: 5pt 10pt;
  border: 2pt solid black;
  background-color: #c0c0c0;
  color: #0000ff;
  font-size: 1.25rem;
  font-weight: bold;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: #a0a0a0;
  }

  &:active {
    background-color: #808080;
  }

  @media only screen and (max-width: 720px) {
    font-size: 1rem;
    padding: 3pt 8pt;
    gap: 3pt;
  }
`;

const GoldIcon = styled.img`
  width: 20px;
  height: 20px;

  @media only screen and (max-width: 720px) {
    width: 16px;
    height: 16px;
  }
`;

const RightSideContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-end;

  gap: 5pt;
`;

type Props = {
  onPressCreate: () => void;
  gameTypes: string[];
};

export default function LobbyHeader(props: Props) {
  const dispatch = _useDispatch();
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);

  const connectedPlayers = _useSelector((state) => state.app.connectedPlayers);

  const playerName = _useSelector((state) => state.app.playerMetadata?.name);
  const playerImg = _useSelector((state) => state.app.playerMetadata?.img);

  const windowSize = useWindowSize();
  const { isConnected } = useAccount();
  const {
    data: dmtBalance,
    isLoading: isBalanceLoading,
    refetch: refetchBalance,
  } = useDMTBalance({ watch: true });

  function disconnectAndGoToLogin() {
    getSocket().disconnect();
    dispatch(setStage({ stage: Stage.LOGIN, stageMeta: {} }));
  }

  function handleProfileSave(name: string, avatarKey: string) {
    profileUpdate({ name, avatarKey });
  }

  function handleBalanceClick() {
    refetchBalance();
  }

  return (
    <Container>
      <LogoContainer>
        <span>¡SANKO!</span>
        <SmallText>{connectedPlayers} players online</SmallText>
      </LogoContainer>
      <RightSideContainer>
        <PlayerMetadata>
          <PlayerInfoLeft>
            <Avatar src={playerImg} crossOrigin='anonymous' />
            <PlayerName>{playerName}</PlayerName>
          </PlayerInfoLeft>
          <EditButton onClick={() => setIsEditPanelOpen(true)}>
            <EditIcon src={pencilEditImg} alt='Edit Profile' />
          </EditButton>
        </PlayerMetadata>
        {isConnected && (
          <BalanceDisplay onClick={handleBalanceClick}>
            {isBalanceLoading ? (
              'Loading...'
            ) : (
              <>
                {dmtBalance
                  ? parseFloat(dmtBalance.formatted).toFixed(2)
                  : '0.00'}
                <GoldIcon src={sankogoldImg} alt='GOLD' />
              </>
            )}
          </BalanceDisplay>
        )}
        <ButtonContainer>
          {windowSize.width && windowSize.width > 375 && (
            <SmallButton onClick={disconnectAndGoToLogin}>LOG OUT</SmallButton>
          )}
          <SmallButton
            onClick={props.onPressCreate}
            disabled={props.gameTypes.includes('tournament')}
          >
            CREATE GAME
          </SmallButton>
        </ButtonContainer>
      </RightSideContainer>
      <EditProfilePanel
        isOpen={isEditPanelOpen}
        onClose={() => setIsEditPanelOpen(false)}
        currentName={playerName || ''}
        currentImg={playerImg || ''}
        onSave={handleProfileSave}
      />
    </Container>
  );
}
